import type { Fields } from '@puckeditor/core';

import { HEX_COLOR_PATTERN } from './hex-color';
import { ColorField } from './widgets/color-field';
import { NumberField } from './widgets/number-field';
import { SelectField } from './widgets/select-field';
import type { CssFieldDescriptor, NumericCssFieldType, SelectCssFieldType, SelectOption } from './types';

const FONT_STYLE_OPTIONS: SelectOption[] = [
  { label: 'Normal', value: 'normal' },
  { label: 'Italic', value: 'italic' },
];

const FONT_WEIGHT_OPTIONS: SelectOption[] = [
  { label: 'Normal (400)', value: '400' },
  { label: 'Medium (500)', value: '500' },
  { label: 'Semi-Bold (600)', value: '600' },
  { label: 'Bold (700)', value: '700' },
];

const OBJECT_FIT_OPTIONS: SelectOption[] = [
  { label: 'Contain', value: 'contain' },
  { label: 'Cover', value: 'cover' },
];

const TEXT_TRANSFORM_OPTIONS: SelectOption[] = [
  { label: 'None', value: 'none' },
  { label: 'Uppercase', value: 'uppercase' },
  { label: 'Capitalize', value: 'capitalize' },
];

const TEXT_ALIGN_OPTIONS: SelectOption[] = [
  { label: 'Left', value: 'left' },
  { label: 'Center', value: 'center' },
  { label: 'Right', value: 'right' },
];

const SELECT_OPTIONS: Record<SelectCssFieldType, SelectOption[]> = {
  fontStyle: FONT_STYLE_OPTIONS,
  fontWeight: FONT_WEIGHT_OPTIONS,
  objectFit: OBJECT_FIT_OPTIONS,
  textTransform: TEXT_TRANSFORM_OPTIONS,
  textAlign: TEXT_ALIGN_OPTIONS,
};

/** Complete CSS hex colors, matching what {@link ColorField} accepts. */
const HEX_COLOR_AI_SCHEMA = { type: 'string', pattern: HEX_COLOR_PATTERN };

/**
 * Maps a CssFieldType to the appropriate Puck custom field widget.
 *
 * Every branch must set `ai.schema`: Puck's AI plugin can infer a schema for built-in
 * field types (text, select, etc.) but not for `custom` ones, since their `render` is an
 * opaque component. Omitting it doesn't just make AI unable to edit *that* field — the
 * AI plugin 400s on the whole request the moment it touches this config, so every
 * `custom` field it can reach needs one.
 */
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
function fieldForType(descriptor: CssFieldDescriptor) {
  const { label, type } = descriptor;

  switch (type) {
    case 'color':
      return {
        type: 'custom' as const,
        label,
        render: ColorField,
        ai: { schema: HEX_COLOR_AI_SCHEMA as Record<string, unknown>, instructions: 'Hex color, e.g. #2c3236.' },
      };

    case 'fontSize':
    case 'spacing':
    case 'borderRadius':
      return {
        type: 'custom' as const,
        label,
        render: NumberField(unitForType(type)),
        ai: { schema: pxAiSchema(), instructions: 'CSS pixel value, e.g. 16px.' },
      };

    case 'fontStyle':
    case 'fontWeight':
    case 'objectFit':
    case 'textTransform':
    case 'textAlign':
      return {
        type: 'custom' as const,
        label,
        render: SelectField(selectOptionsForType(type)),
        ai: { schema: selectAiSchema(selectOptionsForType(type)) },
      };

    default: {
      const _exhaustive: never = type;
      throw new Error(`Unhandled CSS field type: ${String(_exhaustive)}`);
    }
  }
}

/** JSON schema for a `NumberField('px')`-backed value, e.g. '16px'. */
function pxAiSchema(): Record<string, unknown> {
  return { type: 'string', pattern: '^\\d+px$' };
}

/** JSON schema for a `SelectField`-backed value: an enum of the option values. */
function selectAiSchema(options: readonly SelectOption[]): Record<string, unknown> {
  return { type: 'string', enum: options.map((option) => option.value) };
}

function unitForType(_type: NumericCssFieldType): string {
  return 'px';
}

function selectOptionsForType(type: SelectCssFieldType): SelectOption[] {
  return SELECT_OPTIONS[type];
}

/**
 * Generates Puck `fields` and `defaultProps` from an array of CSS field descriptors.
 * Each descriptor becomes a custom field widget in the Puck editing panel.
 */
export function createCssFields(
  descriptors: readonly CssFieldDescriptor[]
): {
  fields: Fields;
  defaultProps: Record<string, string>;
} {
  const fields: Record<string, ReturnType<typeof fieldForType>> = {};
  const defaultProps: Record<string, string> = {};

  descriptors.forEach((descriptor) => {
    fields[descriptor.prop] = fieldForType(descriptor);
    defaultProps[descriptor.prop] = descriptor.defaultValue;
  });

  return { fields, defaultProps };
}
